/**
 * KeyboardCheatSheet — modal listing every keyboard shortcut Beacon
 * understands. Opened by ⌘? or via `setOpen(true)` from any caller.
 *
 * INTERACTION_SPEC §10 line 144: "⌘? opens shortcut cheat-sheet."
 * The full key map is documented there (§10 lines 125–143). This
 * component is the visible mirror of that table.
 *
 * Updates require touching one place: the SECTIONS const below.
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

import { useHotkeys } from '@/hooks/use-hotkeys.js';
import { cn } from '@/lib/cn.js';

interface KeyboardCheatSheetProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutSection {
  title: string;
  rows: ShortcutRow[];
}

interface ShortcutRow {
  keys: string[];
  action: string;
  scope?: string;
}

const SECTIONS: ShortcutSection[] = [
  {
    title: 'Navigation',
    rows: [
      { keys: ['⌘', 'K'], action: 'Command palette', scope: 'Global' },
      { keys: ['G', 'I'], action: 'Go to inbox', scope: 'Global' },
      { keys: ['G', 'R'], action: 'Go to reports', scope: 'Global' },
      { keys: ['G', 'D'], action: 'Go to dashboards', scope: 'Global' },
      { keys: ['J'], action: 'Next in list', scope: 'Triage / queue' },
      { keys: ['K'], action: 'Previous in list', scope: 'Triage / queue' },
      { keys: ['↵'], action: 'Open selected', scope: 'List' },
      { keys: ['Esc'], action: 'Close modal / drawer / focus', scope: 'Global' },
    ],
  },
  {
    title: 'Tickets',
    rows: [
      { keys: ['C'], action: 'Compose new ticket', scope: 'Global' },
      { keys: ['E'], action: 'Resolve ticket', scope: 'Ticket detail' },
      { keys: ['A'], action: 'Assign', scope: 'Ticket detail' },
      { keys: ['S'], action: 'Change status', scope: 'Ticket detail' },
      { keys: ['T'], action: 'Log time', scope: 'Ticket detail' },
      { keys: ['N'], action: 'Switch to internal note', scope: 'Composer' },
      { keys: ['⌘', '↵'], action: 'Send reply / save / submit', scope: 'Composer & forms' },
    ],
  },
  {
    title: 'Help',
    rows: [{ keys: ['⌘', '?'], action: 'Show this cheat sheet', scope: 'Global' }],
  },
];

export function KeyboardCheatSheet({ open, onClose }: KeyboardCheatSheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on Esc, even though useHotkeys above handles it globally —
  // belt and braces in case a caller mounts us without the global hook.
  useHotkeys(
    {
      escape: () => onClose(),
    },
    { disabled: !open, inInput: true }
  );

  // Trap focus + restore on close (INTERACTION_SPEC §9 — "Modal traps
  // focus. Returning focus to trigger on close").
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[4px] transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cheat-sheet-title"
        tabIndex={-1}
        className="relative bg-ink-850 border border-ink-600 rounded-lg shadow-2xl w-[560px] max-w-[92vw] max-h-[80vh] overflow-hidden flex flex-col focus:outline-none"
      >
        <div className="flex items-center px-4 h-11 border-b border-ink-600">
          <h2
            id="cheat-sheet-title"
            className="text-[13px] font-semibold text-ink-100"
          >
            Keyboard shortcuts
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-auto h-7 w-7 inline-flex items-center justify-center rounded text-ink-300 hover:text-ink-100 hover:bg-ink-700 transition-colors focus-ring"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 space-y-5">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h3 className="text-[10.5px] uppercase tracking-[0.12em] text-ink-400 font-semibold mb-2">
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.rows.map((row, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between h-7 px-2 rounded hover:bg-ink-800 transition-colors"
                  >
                    <span className="text-[12.5px] text-ink-100">
                      {row.action}
                    </span>
                    <span className="flex items-center gap-2">
                      {row.scope && (
                        <span className="text-[10.5px] text-ink-400 mono uppercase tracking-wider">
                          {row.scope}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        {row.keys.map((k, j) => (
                          <Kbd key={j}>{k}</Kbd>
                        ))}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <div className="px-4 h-9 border-t border-ink-600 flex items-center justify-between text-[11px] text-ink-400 mono">
          <span>
            Press <Kbd>Esc</Kbd> to close
          </span>
          <span>
            Reopen with <Kbd>⌘</Kbd>
            <Kbd>?</Kbd>
          </span>
        </div>
      </div>
    </div>
  );
}

/** Single keycap. Mono, dim, bordered. */
function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded border border-ink-600 bg-ink-800 text-[11px] mono text-ink-200',
        className
      )}
    >
      {children}
    </kbd>
  );
}
